import {
  getScanPhotoScanVideoId,
  type ScanGroupOptimized,
  type ScanRootOptimized,
} from '@kaiizen/kai-scan-template-system';

import { getGroupTitle } from '@/Client/Lib/scopeNavigatorPartPresentersLib';

export type VideoGroupMarker = {
  groupId: string;
  groupName: string;
  anchorSeconds: number;
  photoId: string;
};

export type VideoGroupSegment = VideoGroupMarker & {
  startSeconds: number;
  endSeconds: number;
};

const findGroupVideoOffset = (
  group: ScanGroupOptimized,
  scanVideoId: string,
) =>
  group.videos?.find(
    video => video.videoId === scanVideoId && video.videoOffset,
  )?.videoOffset;

/** Earliest group photo for this walkthrough — filmstrip decoration only, not segment bounds. */
const findRepresentativeGroupPhoto = (
  group: ScanGroupOptimized,
  scanVideoId: string,
): { photoId: string; videoFrameTimeSeconds?: number } | undefined => {
  let representative: { photoId: string; videoFrameTimeSeconds?: number } | undefined;

  for (const photo of group.photos ?? []) {
    if (getScanPhotoScanVideoId(photo) !== scanVideoId) continue;

    const frameTime = photo.videoFrameTimeSeconds;
    if (
      representative
      && frameTime != null
      && representative.videoFrameTimeSeconds != null
      && representative.videoFrameTimeSeconds <= frameTime
    ) {
      continue;
    }

    representative = {
      photoId: photo.photoId,
      videoFrameTimeSeconds: frameTime,
    };
  }

  return representative;
};

const findEarliestGroupPhotoMarker = (
  group: ScanGroupOptimized,
  scanVideoId: string,
): VideoGroupMarker | undefined => {
  const representative = findRepresentativeGroupPhoto(group, scanVideoId);
  if (!representative || representative.videoFrameTimeSeconds == null) return undefined;

  return {
    groupId: group.id,
    groupName: getGroupTitle(group),
    anchorSeconds: representative.videoFrameTimeSeconds,
    photoId: representative.photoId,
  };
};

/**
 * Continuous playback ranges from photo `videoFrameTimeSeconds` transition points.
 * Legacy fallback when no `group.videos[].videoOffset` exists for this walkthrough.
 */
export const buildVideoGroupSegmentsFromPhotoAnchors = (
  markers: readonly VideoGroupMarker[],
  durationSeconds: number,
): VideoGroupSegment[] => {
  if (!markers.length || durationSeconds <= 0) return [];

  const sortedMarkers = [...markers].sort((left, right) => left.anchorSeconds - right.anchorSeconds);

  return sortedMarkers.map((marker, index) => ({
    ...marker,
    startSeconds: index === 0 ? 0 : marker.anchorSeconds,
    endSeconds: index === sortedMarkers.length - 1
      ? durationSeconds
      : sortedMarkers[index + 1].anchorSeconds,
  }));
};

const collectPhotoAnchorMarkers = (
  scanRoot: ScanRootOptimized,
  scanVideoId: string,
): VideoGroupMarker[] => {
  const markers: VideoGroupMarker[] = [];

  for (const group of scanRoot.groups ?? []) {
    const photoMarker = findEarliestGroupPhotoMarker(group, scanVideoId);
    if (photoMarker) {
      markers.push(photoMarker);
    }
  }

  return markers;
};

/** Raw walk-event ranges from `group.videos[].videoOffset` (may overlap or leave gaps). */
export const collectVideoGroupSegmentRanges = (
  scanRoot: ScanRootOptimized | undefined,
  scanVideoId: string | undefined,
  durationSeconds: number,
): VideoGroupSegment[] => {
  if (!scanRoot || !scanVideoId) return [];

  const ranges: VideoGroupSegment[] = [];

  for (const group of scanRoot.groups ?? []) {
    const videoOffset = findGroupVideoOffset(group, scanVideoId);
    if (!videoOffset) continue;

    const { startSeconds, endSeconds } = videoOffset;
    const representativePhoto = findRepresentativeGroupPhoto(group, scanVideoId);

    ranges.push({
      groupId: group.id,
      groupName: getGroupTitle(group),
      startSeconds,
      endSeconds: durationSeconds > 0 ? Math.min(endSeconds, durationSeconds) : endSeconds,
      anchorSeconds: startSeconds,
      photoId: representativePhoto?.photoId ?? '',
    });
  }

  return ranges.sort((left, right) => left.startSeconds - right.startSeconds);
};

/**
 * Turns raw offset ranges into continuous timeline bands for labeling.
 * Boundaries sit at each group's `videoOffset.startSeconds`; the first band
 * starts at 0 and the last ends at duration (same model as photo anchors).
 */
export const buildContinuousTimelineSegmentsFromRanges = (
  ranges: readonly VideoGroupSegment[],
  durationSeconds: number,
): VideoGroupSegment[] => {
  if (!ranges.length || durationSeconds <= 0) return [];

  const sortedRanges = [...ranges].sort((left, right) => left.startSeconds - right.startSeconds);

  return sortedRanges.map((range, index) => ({
    ...range,
    anchorSeconds: range.startSeconds,
    startSeconds: index === 0 ? 0 : range.startSeconds,
    endSeconds: index === sortedRanges.length - 1
      ? durationSeconds
      : sortedRanges[index + 1].startSeconds,
  }));
};

/**
 * Builds continuous timeline segments for the scrubber UI.
 *
 * Primary: `videoOffset.startSeconds` transition points from walk events.
 * Fallback: photo `videoFrameTimeSeconds` anchors when no video offsets exist.
 */
export const collectVideoGroupSegments = (
  scanRoot: ScanRootOptimized | undefined,
  scanVideoId: string | undefined,
  durationSeconds: number,
): VideoGroupSegment[] => {
  if (!scanRoot || !scanVideoId) return [];

  const videoOffsetRanges = collectVideoGroupSegmentRanges(scanRoot, scanVideoId, durationSeconds);
  if (videoOffsetRanges.length) {
    return buildContinuousTimelineSegmentsFromRanges(videoOffsetRanges, durationSeconds);
  }

  const photoAnchorMarkers = collectPhotoAnchorMarkers(scanRoot, scanVideoId);
  return buildVideoGroupSegmentsFromPhotoAnchors(photoAnchorMarkers, durationSeconds);
};

export const getActiveVideoGroupSegment = (
  segments: readonly VideoGroupSegment[],
  currentTimeSeconds: number,
): VideoGroupSegment | undefined => {
  if (!segments.length) return undefined;

  const clampedTime = Math.max(0, currentTimeSeconds);
  const nonLastMatch = segments.find(
    segment => clampedTime >= segment.startSeconds && clampedTime < segment.endSeconds,
  );
  if (nonLastMatch) return nonLastMatch;

  const lastSegment = segments[segments.length - 1];
  return clampedTime >= lastSegment.startSeconds && clampedTime <= lastSegment.endSeconds
    ? lastSegment
    : undefined;
};

/** Stable accent hues for up to twelve groups on one walkthrough. */
export const VIDEO_GROUP_SEGMENT_COLORS = [
  '#4A7FC1',
  '#5A9E6F',
  '#C17B4A',
  '#8B6BB8',
  '#C14A6A',
  '#4AA8A0',
  '#B8A04A',
  '#6B8FC1',
  '#9E5A7F',
  '#7FC14A',
  '#C14A4A',
  '#4A6FC1',
] as const;

export const getVideoGroupSegmentColor = (index: number): string => VIDEO_GROUP_SEGMENT_COLORS[index % VIDEO_GROUP_SEGMENT_COLORS.length];

/** Played fraction within one group's timeline band (0 at segment start, 1 at segment end). */
export const getGroupSegmentPlayedFraction = (
  segment: VideoGroupSegment,
  currentTimeSeconds: number,
): number => {
  const spanSeconds = segment.endSeconds - segment.startSeconds;
  if (spanSeconds <= 0) return 0;
  return Math.max(0, Math.min(1, (currentTimeSeconds - segment.startSeconds) / spanSeconds));
};
